import React from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Checkbox } from '../ui/checkbox';
import { Calculator, FileCheck, CheckCircle, ArrowUpCircle, ArrowDownCircle, Landmark, Scale, Plus, XCircle, Trash2, Hourglass } from 'lucide-react';
import { fmtDate as fmtDateTz } from '../../lib/datetime';

const today = () => new Date().toISOString().slice(0, 10);
const formatCurrency = (amount, currency = 'ARS') =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount || 0);
const formatDate = (d) => (d ? fmtDateTz(d) : '-');

// v3.34.11 — Parse flexible AR/EN para filtros de importe en la grilla de conciliación
const parseLocaleNumber = (val) => {
  if (val === null || val === undefined) return NaN;
  const s = String(val).trim();
  if (!s) return NaN;
  // Reglas:
  //  - Si contiene coma → formato AR: quitar puntos (miles), pasar coma a punto.
  //  - Si contiene 2+ puntos y nada de coma → AR puro con separador de miles ("1.000.000" = 1000000).
  //  - Si contiene exactamente 1 punto → EN decimal (dejar como está).
  let normalized;
  if (s.includes(',')) {
    normalized = s.replace(/\./g, '').replace(',', '.');
  } else {
    const dotCount = (s.match(/\./g) || []).length;
    normalized = dotCount >= 2 ? s.replace(/\./g, '') : s;
  }
  const n = parseFloat(normalized);
  return Number.isFinite(n) ? n : NaN;
};

export function CashCountDialog({ open, onOpenChange, form, setForm, physicalCashes, onCancel, onCreate }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-blue-500" />
            Nuevo Arqueo de Caja
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Fecha *</Label>
            <Input type="date" max={today()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <Label>Caja *</Label>
            <select value={form.cash_bank_id} onChange={(e) => setForm({ ...form, cash_bank_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Seleccionar caja...</option>
              {physicalCashes.map(cb => (
                <option key={cb.cash_bank_id} value={cb.cash_bank_id}>{cb.name} - Saldo: {formatCurrency(cb.current_balance, cb.currency)}</option>
              ))}
            </select>
          </div>
          <div>
            <Label>Saldo Físico Contado *</Label>
            <Input type="number" step="0.01" value={form.physical_balance} onChange={(e) => setForm({ ...form, physical_balance: e.target.value })} placeholder="0.00" />
          </div>
          <div>
            <Label>Notas</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button onClick={onCreate}>Crear Arqueo</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// v3.34.0 — Dialog de cierre con múltiples ajustes justificativos
export function CloseCashCountDialog({
  open, onOpenChange, cashCount, adjustments = [],
  closeForm, setCloseForm,
  incomeConcepts, expenseConcepts,
  onAddAdjustment, onRemoveAdjustment,
  onCancel, onClose, submitting = false,
}) {
  const [newAdj, setNewAdj] = React.useState({ type: 'income', concept_id: '', amount: '', description: '' });
  const [adjSubmitting, setAdjSubmitting] = React.useState(false);

  React.useEffect(() => {
    if (open) setNewAdj({ type: 'income', concept_id: '', amount: '', description: '' });
  }, [open]);

  if (!cashCount) return null;

  // Suma firmada de ajustes previos (debit=+ / credit=-)
  const signedSum = (adjustments || []).reduce((s, a) => {
    const amt = Number(a.amount || 0);
    return s + (a.adjustment_type === 'debit' ? amt : -amt);
  }, 0);
  const diff = Number(cashCount.difference || 0);
  const residual = Math.round((diff - signedSum) * 100) / 100;

  const handleAdd = async () => {
    if (!newAdj.concept_id || !newAdj.amount) return;
    const amount = parseFloat(newAdj.amount);
    if (!(amount > 0)) return;
    setAdjSubmitting(true);
    try {
      await onAddAdjustment({ ...newAdj, amount });
      setNewAdj({ type: newAdj.type, concept_id: '', amount: '', description: '' });
    } finally {
      setAdjSubmitting(false);
    }
  };

  const concepts = newAdj.type === 'income' ? incomeConcepts : expenseConcepts;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="close-cash-count-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileCheck className="w-5 h-5 text-green-500" />
            Cerrar Arqueo {cashCount.cash_count_number}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Resumen */}
          <div className="p-4 bg-muted rounded-lg space-y-2">
            <p className="text-sm"><strong>Caja:</strong> {cashCount.cash_bank_name}</p>
            <div className="grid grid-cols-3 gap-4 mt-3">
              <div>
                <p className="text-xs text-muted-foreground">Saldo Teórico (inicial)</p>
                <p className="text-base font-semibold">{formatCurrency(cashCount.theoretical_balance)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Saldo Físico</p>
                <p className="text-base font-semibold">{formatCurrency(cashCount.physical_balance)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Diferencia bruta</p>
                {diff === 0 ? (
                  <p className="text-base font-semibold text-green-600">$0,00</p>
                ) : diff > 0 ? (
                  <p className="text-base font-semibold text-blue-600 flex items-center gap-1"><ArrowUpCircle className="w-4 h-4" />+{formatCurrency(diff)}</p>
                ) : (
                  <p className="text-base font-semibold text-red-600 flex items-center gap-1"><ArrowDownCircle className="w-4 h-4" />{formatCurrency(diff)}</p>
                )}
              </div>
            </div>
          </div>

          {/* Ajustes cargados */}
          <div>
            <p className="text-sm font-medium mb-2">Ajustes justificativos ({adjustments.length})</p>
            {adjustments.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Aún no cargaste ningún ajuste. Si no corresponde, la diferencia bruta se registrará como "diferencia de caja".</p>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">N°</TableHead>
                      <TableHead className="text-xs">Tipo</TableHead>
                      <TableHead className="text-xs">Concepto</TableHead>
                      <TableHead className="text-xs">Descripción</TableHead>
                      <TableHead className="text-xs text-right">Monto</TableHead>
                      <TableHead className="text-xs text-center w-16">Quitar</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adjustments.map(a => (
                      <TableRow key={a.adjustment_id} data-testid={`cc-adj-row-${a.adjustment_number}`}>
                        <TableCell className="text-xs font-mono">{a.adjustment_number}</TableCell>
                        <TableCell className="text-xs">
                          {a.adjustment_type === 'debit'
                            ? <Badge className="bg-blue-100 text-blue-700">Ingreso</Badge>
                            : <Badge className="bg-red-100 text-red-700">Egreso</Badge>}
                        </TableCell>
                        <TableCell className="text-xs">{a.concept_name}</TableCell>
                        <TableCell className="text-xs max-w-[200px] truncate" title={a.description}>{a.description}</TableCell>
                        <TableCell className="text-xs text-right font-mono">
                          {a.adjustment_type === 'debit' ? '+' : '-'}{formatCurrency(a.amount)}
                        </TableCell>
                        <TableCell className="text-center">
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700"
                                  onClick={() => onRemoveAdjustment(a.adjustment_id)}
                                  data-testid={`cc-adj-remove-${a.adjustment_number}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Agregar ajuste */}
          <div className="border rounded-md p-3 bg-slate-50">
            <p className="text-sm font-medium mb-2 flex items-center gap-1"><Plus className="w-4 h-4" />Agregar ajuste justificativo</p>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <Label className="text-xs">Tipo</Label>
                <select value={newAdj.type}
                        onChange={e => setNewAdj({ ...newAdj, type: e.target.value, concept_id: '' })}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                        data-testid="cc-new-adj-type">
                  <option value="income">Ingreso</option>
                  <option value="expense">Egreso</option>
                </select>
              </div>
              <div>
                <Label className="text-xs">Concepto</Label>
                <select value={newAdj.concept_id}
                        onChange={e => setNewAdj({ ...newAdj, concept_id: e.target.value })}
                        className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                        data-testid="cc-new-adj-concept">
                  <option value="">Seleccionar...</option>
                  {concepts.map(c => <option key={c.concept_id} value={c.concept_id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-xs">Monto</Label>
                <Input type="number" step="0.01" min="0.01" value={newAdj.amount}
                       onChange={e => setNewAdj({ ...newAdj, amount: e.target.value })}
                       className="h-9" placeholder="0.00"
                       data-testid="cc-new-adj-amount" />
              </div>
              <div>
                <Label className="text-xs">Descripción (opc.)</Label>
                <Input value={newAdj.description}
                       onChange={e => setNewAdj({ ...newAdj, description: e.target.value })}
                       className="h-9" placeholder="Ej: billete faltante caja chica"
                       data-testid="cc-new-adj-description" />
              </div>
            </div>
            <div className="mt-2 flex justify-end">
              <Button size="sm" onClick={handleAdd}
                      disabled={!newAdj.concept_id || !newAdj.amount || adjSubmitting}
                      data-testid="cc-new-adj-add-btn">
                <Plus className="w-4 h-4 mr-1" />
                {adjSubmitting ? 'Agregando…' : 'Agregar ajuste'}
              </Button>
            </div>
          </div>

          {/* Residual + concepto */}
          <div className="border-t pt-3">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Suma de ajustes</p>
              <p className="text-sm font-mono">{signedSum >= 0 ? '+' : ''}{formatCurrency(signedSum)}</p>
            </div>
            <div className="flex items-center justify-between mt-1">
              <p className="text-sm font-medium">Diferencia residual sin justificar</p>
              {Math.abs(residual) < 0.01 ? (
                <p className="text-sm font-bold text-green-600 flex items-center gap-1"><CheckCircle className="w-4 h-4" />$0,00</p>
              ) : residual > 0 ? (
                <p className="text-sm font-bold text-blue-600">+{formatCurrency(residual)} (sobrante)</p>
              ) : (
                <p className="text-sm font-bold text-red-600">{formatCurrency(residual)} (faltante)</p>
              )}
            </div>

            {Math.abs(residual) >= 0.01 && (
              <div className="mt-3">
                <p className="text-xs text-muted-foreground mb-1">
                  {residual > 0
                    ? 'Concepto de ingreso para registrar el sobrante residual como "diferencia de caja":'
                    : 'Concepto de gasto para registrar el faltante residual como "diferencia de caja":'}
                </p>
                {residual > 0 ? (
                  <select value={closeForm.income_concept_id}
                          onChange={e => setCloseForm({ ...closeForm, income_concept_id: e.target.value })}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                          data-testid="cc-residual-income-concept">
                    <option value="">Seleccionar concepto de ingreso...</option>
                    {incomeConcepts.map(c => <option key={c.concept_id} value={c.concept_id}>{c.name}</option>)}
                  </select>
                ) : (
                  <select value={closeForm.expense_concept_id}
                          onChange={e => setCloseForm({ ...closeForm, expense_concept_id: e.target.value })}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm"
                          data-testid="cc-residual-expense-concept">
                    <option value="">Seleccionar concepto de gasto...</option>
                    {expenseConcepts.map(c => <option key={c.concept_id} value={c.concept_id}>{c.name}</option>)}
                  </select>
                )}
              </div>
            )}

            <div className="mt-3 p-2 rounded-md bg-amber-50 border border-amber-200 text-[11px] text-amber-900">
              Al confirmar: se cerrará el arqueo, se bloqueará (<b>bank_lock</b>) la caja/fecha. Un admin puede reabrir luego para quitar o agregar ajustes.
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>Cancelar</Button>
          <Button onClick={onClose} disabled={submitting} data-testid="cc-confirm-close-btn">
            {submitting ? 'Cerrando…' : 'Confirmar y cerrar arqueo'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ReconciliationDialog({ open, onOpenChange, form, setForm, bankAccounts, onCancel, onCreate }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="w-5 h-5 text-purple-500" />
            Nueva Conciliación Bancaria
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Fecha *</Label>
            <Input type="date" max={today()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <Label>Cuenta Bancaria *</Label>
            <select value={form.cash_bank_id} onChange={(e) => setForm({ ...form, cash_bank_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm">
              <option value="">Seleccionar cuenta...</option>
              {bankAccounts.map(cb => <option key={cb.cash_bank_id} value={cb.cash_bank_id}>{cb.name} - Saldo: {formatCurrency(cb.current_balance, cb.currency)}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Período Desde *</Label>
              <Input type="date" max={today()} value={form.period_start} onChange={(e) => setForm({ ...form, period_start: e.target.value })} />
            </div>
            <div>
              <Label>Período Hasta *</Label>
              <Input type="date" max={today()} value={form.period_end} onChange={(e) => setForm({ ...form, period_end: e.target.value })} />
            </div>
          </div>
          <div>
            <Label>Saldo según Extracto Bancario *</Label>
            <Input
              type="text"
              inputMode="decimal"
              value={form.bank_statement_balance}
              onChange={(e) => setForm({ ...form, bank_statement_balance: e.target.value })}
              placeholder="0,00 o 0.00 (acepta ambos formatos)"
              data-testid="reconciliation-bank-statement-balance"
            />
            <p className="text-[11px] text-muted-foreground mt-1">Acepta coma o punto decimal · ejemplo: 4.173.820,66 o 4173820.66</p>
          </div>
          <div>
            <Label>Notas</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button onClick={onCreate}>Crear Conciliación</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function ViewReconciliationDialog({ open, onOpenChange, reconciliation, onAddItem, onToggleReconciled, onSetTransit, onDeleteItem, canReconcile = true, canComplete, onComplete, onClose }) {
  const transitCredits = reconciliation?.transit_credits || 0;
  const transitDebits = reconciliation?.transit_debits || 0;
  const hasTransits = transitCredits > 0 || transitDebits > 0;
  // v3.26.1 — fórmula corregida: bank = system + cheques_no_cobrados − depositos_en_transito
  const expectedBank = (reconciliation?.system_balance || 0) + transitCredits - transitDebits;

  // v3.34.11 — Filtros por columna (solo acotan la lista visible, no recalculan saldos)
  const emptyFilters = {
    date_from: '', date_to: '',
    type: '',
    reference: '',
    description: '',
    debit_val: '',
    credit_val: '',
    status: '',
  };
  const [filters, setFilters] = React.useState(emptyFilters);
  React.useEffect(() => { if (open) setFilters(emptyFilters); }, [open, reconciliation?.reconciliation_id]); // eslint-disable-line react-hooks/exhaustive-deps
  const upd = (patch) => setFilters(prev => ({ ...prev, ...patch }));
  const clearFilters = () => setFilters(emptyFilters);
  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  const allItems = reconciliation?.items || [];
  const typeOptions = React.useMemo(() => {
    const set = new Set();
    allItems.forEach(it => {
      const lbl = it.movement_type_label || (it.item_type === 'reconciliation_adjustment' ? 'Ajuste Conciliación' : it.item_type === 'system_movement' ? 'Sistema' : 'Extracto');
      if (lbl) set.add(lbl);
    });
    return Array.from(set).sort();
  }, [allItems]);

  const cmpAmount = (val, target) => {
    if (!(val > 0)) return false;
    return Math.abs(val - target) < 0.01;
  };

  const filteredItems = React.useMemo(() => {
    return allItems.filter(item => {
      // Fecha
      if (filters.date_from && (!item.date || item.date < filters.date_from)) return false;
      if (filters.date_to && (!item.date || item.date > filters.date_to)) return false;
      // Tipo
      if (filters.type) {
        const lbl = item.movement_type_label || (item.item_type === 'reconciliation_adjustment' ? 'Ajuste Conciliación' : item.item_type === 'system_movement' ? 'Sistema' : 'Extracto');
        if (lbl !== filters.type) return false;
      }
      // N° Comprobante
      if (filters.reference) {
        const ref = String(item.reference_number || '').toLowerCase();
        if (!ref.includes(filters.reference.toLowerCase())) return false;
      }
      // Descripción
      if (filters.description) {
        const desc = String(item.description || '').toLowerCase();
        if (!desc.includes(filters.description.toLowerCase())) return false;
      }
      // Débito (ingresos: is_credit true) — match exacto con tolerancia centavo
      if (filters.debit_val !== '') {
        const target = parseLocaleNumber(filters.debit_val);
        if (!Number.isNaN(target)) {
          const val = Number(item.debit || (item.is_credit ? item.amount : 0)) || 0;
          if (!cmpAmount(val, target)) return false;
        }
      }
      // Crédito (egresos: is_credit false) — match exacto con tolerancia centavo
      if (filters.credit_val !== '') {
        const target = parseLocaleNumber(filters.credit_val);
        if (!Number.isNaN(target)) {
          const val = Number(item.credit || (!item.is_credit ? item.amount : 0)) || 0;
          if (!cmpAmount(val, target)) return false;
        }
      }
      // Estado
      if (filters.status) {
        if (filters.status === 'reconciled' && !item.is_reconciled) return false;
        if (filters.status === 'pending' && (item.is_reconciled || item.is_in_transit)) return false;
        if (filters.status === 'transit' && !item.is_in_transit) return false;
      }
      return true;
    });
  }, [allItems, filters]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-purple-500" />
            Conciliación {reconciliation?.reconciliation_number}
            {reconciliation?.status === 'draft' && <Badge className="bg-yellow-100 text-yellow-800 ml-2">Borrador</Badge>}
            {reconciliation?.status === 'completed' && <Badge className="bg-green-100 text-green-800 ml-2">Completada</Badge>}
          </DialogTitle>
        </DialogHeader>
        {reconciliation && (
          <div className="space-y-4">
            <div className="text-sm text-muted-foreground">
              {reconciliation.cash_bank_name} | Período: {formatDate(reconciliation.period_start)} - {formatDate(reconciliation.period_end)}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Card><CardContent className="pt-4 pb-3"><p className="text-xs text-muted-foreground">Saldo Inicial</p><p className="text-base font-bold">{formatCurrency(reconciliation.starting_balance || 0)}</p><p className="text-[10px] text-muted-foreground">Suma de movs previos al período</p></CardContent></Card>
              <Card><CardContent className="pt-4 pb-3"><p className="text-xs text-muted-foreground">Saldo Sistema</p><p className="text-base font-bold">{formatCurrency(reconciliation.system_balance)}</p><p className="text-[10px] text-muted-foreground">Inicial + Movimientos del período</p></CardContent></Card>
              <Card><CardContent className="pt-4 pb-3"><p className="text-xs text-muted-foreground">Saldo Extracto Bancario</p><p className="text-base font-bold text-blue-600">{formatCurrency(reconciliation.bank_statement_balance)}</p></CardContent></Card>
              <Card className={Math.abs(reconciliation.difference) <= 0.01 ? 'border-green-300 bg-green-50' : 'border-orange-300 bg-orange-50'}>
                <CardContent className="pt-4 pb-3">
                  <p className="text-xs text-muted-foreground">Diferencia</p>
                  <p className={`text-base font-bold ${Math.abs(reconciliation.difference) <= 0.01 ? 'text-green-600' : 'text-orange-600'}`}>{formatCurrency(reconciliation.difference)}</p>
                  {Math.abs(reconciliation.difference) > 0.01 && <p className="text-[10px] text-orange-600">Debe ser $0 para cerrar</p>}
                </CardContent>
              </Card>
            </div>

            {/* v3.26.0 — Panel de conciliación bancaria con transitorias */}
            {hasTransits && (
              <Card className="bg-indigo-50/60 border-indigo-200 dark:bg-indigo-950/20 dark:border-indigo-900">
                <CardContent className="pt-4 pb-3 space-y-1 text-sm">
                  <p className="font-semibold flex items-center gap-2 text-indigo-900 dark:text-indigo-200"><Hourglass className="w-4 h-4" />Ajustes por diferencias transitorias</p>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
                    <div>Saldo Sistema: <b>{formatCurrency(reconciliation.system_balance)}</b></div>
                    <div>(+) Cheques emitidos no cobrados: <b className="text-emerald-700">{formatCurrency(transitCredits)}</b></div>
                    <div>(−) Depósitos en tránsito: <b className="text-red-700">{formatCurrency(transitDebits)}</b></div>
                    <div>= Saldo esperado banco: <b className="text-blue-700">{formatCurrency(expectedBank)}</b></div>
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="flex justify-between items-center">
              <h3 className="font-semibold">
                Movimientos del Sistema
                <span className="ml-2 text-xs font-normal text-muted-foreground" data-testid="reconc-items-count">
                  (Mostrando {filteredItems.length} de {allItems.length})
                </span>
              </h3>
              <div className="flex items-center gap-2">
                {hasActiveFilters && (
                  <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="reconc-clear-filters-btn">
                    <XCircle className="w-4 h-4 mr-1" />Limpiar filtros
                  </Button>
                )}
                {reconciliation.status === 'draft' && canReconcile && (
                  <Button variant="outline" size="sm" onClick={onAddItem} data-testid="add-bank-movement-btn">
                    <Plus className="w-4 h-4 mr-1" />Agregar Movimiento Bancario
                  </Button>
                )}
              </div>
            </div>
            {Math.abs(reconciliation.difference) > 0.01 && reconciliation.status === 'draft' && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800">
                <strong>Diferencia pendiente:</strong> El extracto muestra {formatCurrency(reconciliation.bank_statement_balance)} y el saldo esperado del banco es {formatCurrency(expectedBank)}.
                Cargá los movimientos del extracto no registrados (gastos bancarios, intereses, IIBB, SIRCREB) o marcá como <b>"En tránsito"</b> los cheques emitidos no cobrados / depósitos en tránsito. Se arrastran a la próxima conciliación.
              </div>
            )}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>N° Comprobante</TableHead>
                  <TableHead>Descripción</TableHead>
                  <TableHead className="text-right">Débito</TableHead>
                  <TableHead className="text-right">Crédito</TableHead>
                  <TableHead className="text-center">Estado</TableHead>
                  <TableHead className="text-center w-20">Acciones</TableHead>
                </TableRow>
                {/* v3.34.11 — Fila de filtros por columna */}
                <TableRow className="bg-slate-50/60 hover:bg-slate-50/60">
                  <TableHead className="w-10"></TableHead>
                  <TableHead className="py-1">
                    <div className="flex flex-col gap-1">
                      <Input type="date" value={filters.date_from} onChange={(e) => upd({ date_from: e.target.value })}
                             className="h-7 text-[11px] px-1" placeholder="Desde" title="Fecha desde"
                             data-testid="reconc-filter-date-from" />
                      <Input type="date" value={filters.date_to} onChange={(e) => upd({ date_to: e.target.value })}
                             className="h-7 text-[11px] px-1" placeholder="Hasta" title="Fecha hasta"
                             data-testid="reconc-filter-date-to" />
                    </div>
                  </TableHead>
                  <TableHead className="py-1">
                    <select value={filters.type} onChange={(e) => upd({ type: e.target.value })}
                            className="flex h-7 w-full rounded-md border border-input bg-background px-1 text-[11px]"
                            data-testid="reconc-filter-type">
                      <option value="">Todos</option>
                      {typeOptions.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </TableHead>
                  <TableHead className="py-1">
                    <Input type="text" value={filters.reference} onChange={(e) => upd({ reference: e.target.value })}
                           className="h-7 text-[11px] px-2" placeholder="Buscar…"
                           data-testid="reconc-filter-reference" />
                  </TableHead>
                  <TableHead className="py-1">
                    <Input type="text" value={filters.description} onChange={(e) => upd({ description: e.target.value })}
                           className="h-7 text-[11px] px-2" placeholder="Buscar…"
                           data-testid="reconc-filter-description" />
                  </TableHead>
                  <TableHead className="py-1">
                    <Input type="text" inputMode="decimal" value={filters.debit_val}
                           onChange={(e) => upd({ debit_val: e.target.value })}
                           className="h-7 text-[11px] px-2 text-right" placeholder="Importe exacto"
                           data-testid="reconc-filter-debit-val" />
                  </TableHead>
                  <TableHead className="py-1">
                    <Input type="text" inputMode="decimal" value={filters.credit_val}
                           onChange={(e) => upd({ credit_val: e.target.value })}
                           className="h-7 text-[11px] px-2 text-right" placeholder="Importe exacto"
                           data-testid="reconc-filter-credit-val" />
                  </TableHead>
                  <TableHead className="py-1">
                    <select value={filters.status} onChange={(e) => upd({ status: e.target.value })}
                            className="flex h-7 w-full rounded-md border border-input bg-background px-1 text-[11px]"
                            data-testid="reconc-filter-status">
                      <option value="">Todos</option>
                      <option value="reconciled">Conciliados</option>
                      <option value="pending">Pendientes</option>
                      <option value="transit">En tránsito</option>
                    </select>
                  </TableHead>
                  <TableHead className="py-1 text-center">
                    {hasActiveFilters ? (
                      <button type="button" onClick={clearFilters}
                              className="text-[11px] text-slate-600 hover:text-slate-900 underline"
                              title="Limpiar filtros" data-testid="reconc-clear-filters-inline">
                        Limpiar
                      </button>
                    ) : (
                      <span className="text-muted-foreground text-[11px]">—</span>
                    )}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map(item => {
                  const rowCls = item.is_in_transit
                    ? 'bg-indigo-50 dark:bg-indigo-950/20'
                    : (item.is_reconciled ? 'bg-green-50' : (item.item_type === 'reconciliation_adjustment' ? 'bg-blue-50' : ''));
                  const canDeleteManualAdj = reconciliation.status === 'draft'
                                              && canReconcile
                                              && item.item_type === 'reconciliation_adjustment'
                                              && typeof onDeleteItem === 'function';
                  return (
                    <TableRow key={item.item_id} className={rowCls}>
                      <TableCell>{item.is_credit ? <ArrowUpCircle className="w-4 h-4 text-green-500" /> : <ArrowDownCircle className="w-4 h-4 text-red-500" />}</TableCell>
                      <TableCell className="text-sm">
                        {formatDate(item.date)}
                        {item.inherited_from_reconciliation_id && (
                          <Badge variant="outline" className="ml-1 text-[9px] py-0 px-1 border-indigo-400 text-indigo-700">heredado</Badge>
                        )}
                      </TableCell>
                      <TableCell><Badge variant="outline" className="text-xs">{item.movement_type_label || (item.item_type === 'reconciliation_adjustment' ? 'Ajuste Conciliación' : item.item_type === 'system_movement' ? 'Sistema' : 'Extracto')}</Badge></TableCell>
                      <TableCell className="text-xs font-mono text-slate-700" data-testid={`reconc-ref-${item.item_id}`}>
                        {item.reference_number || <span className="text-muted-foreground">—</span>}
                      </TableCell>
                      <TableCell className="text-sm">
                        {item.description}
                        {item.is_in_transit && (
                          <div className="text-[10px] text-indigo-700 mt-0.5">
                            <Hourglass className="w-3 h-3 inline mr-0.5" />
                            {item.transit_category === 'check_no_cashed' && 'Cheque emitido no cobrado'}
                            {item.transit_category === 'deposit_in_transit' && 'Depósito en tránsito'}
                            {item.transit_category === 'other' && `Otra: ${item.transit_notes || ''}`}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-right font-medium text-green-600">{(item.debit || (item.is_credit ? item.amount : 0)) > 0 ? formatCurrency(item.debit || item.amount) : ''}</TableCell>
                      <TableCell className="text-right font-medium text-red-600">{(item.credit || (!item.is_credit ? item.amount : 0)) > 0 ? formatCurrency(item.credit || item.amount) : ''}</TableCell>
                      <TableCell className="text-center">
                        {reconciliation.status === 'draft' ? (
                          <div className="flex items-center justify-center gap-2">
                            <label className="flex items-center gap-1 text-xs cursor-pointer" title="Aparece en el extracto (conciliado)">
                              <Checkbox
                                checked={item.is_reconciled}
                                disabled={!canReconcile || item.is_in_transit}
                                onCheckedChange={() => onToggleReconciled(item)}
                              />
                              <span className="text-green-700 hidden md:inline">Concil.</span>
                            </label>
                            <button
                              type="button"
                              disabled={!canReconcile || item.is_reconciled}
                              onClick={() => {
                                if (item.is_in_transit) {
                                  onSetTransit?.(item, false, null, null);
                                  return;
                                }
                                const cat = window.prompt('Categoría de la diferencia transitoria:\n1 = Cheque emitido no cobrado\n2 = Depósito en tránsito\n3 = Otra', '1');
                                if (!cat) return;
                                const map = { '1': 'check_no_cashed', '2': 'deposit_in_transit', '3': 'other' };
                                const category = map[cat.trim()] || 'other';
                                let notes = null;
                                if (category === 'other') {
                                  notes = window.prompt('Ingresá el motivo de la diferencia transitoria:');
                                  if (!notes || !notes.trim()) { alert('Motivo obligatorio para "Otra"'); return; }
                                }
                                onSetTransit?.(item, true, category, notes);
                              }}
                              className={`text-[11px] px-2 py-0.5 rounded border transition-colors ${
                                item.is_in_transit
                                  ? 'bg-indigo-600 text-white border-indigo-700 hover:bg-indigo-700'
                                  : 'border-indigo-300 text-indigo-700 hover:bg-indigo-100 disabled:opacity-40 disabled:cursor-not-allowed'
                              }`}
                              data-testid={`transit-${item.item_id}`}
                              title={item.is_in_transit ? 'Quitar de tránsito' : 'Marcar como diferencia transitoria'}
                            >
                              <Hourglass className="w-3 h-3 inline mr-0.5" />
                              {item.is_in_transit ? 'En tránsito ✓' : 'En tránsito'}
                            </button>
                          </div>
                        ) : (
                          item.is_reconciled
                            ? <CheckCircle className="w-4 h-4 text-green-500 mx-auto" />
                            : item.is_in_transit
                              ? <Hourglass className="w-4 h-4 text-indigo-500 mx-auto" />
                              : <XCircle className="w-4 h-4 text-red-500 mx-auto" />
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {canDeleteManualAdj ? (
                          <Button size="icon" variant="ghost" className="h-7 w-7 text-red-500 hover:text-red-700"
                                  onClick={() => onDeleteItem(item)}
                                  title="Eliminar este ajuste manual (revierte saldo y asiento contable)"
                                  data-testid={`delete-reconc-adj-${item.item_id}`}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        ) : (
                          <span className="text-muted-foreground text-xs">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
                {(!filteredItems || filteredItems.length === 0) && (
                  <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    {allItems.length === 0 ? 'No hay movimientos en este período' : 'Ningún movimiento coincide con los filtros aplicados'}
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>

            {/* v3.34.6 — Zócalo de auditoría (misma estética que ViewCashCountDialog) */}
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="pt-3 pb-3 text-xs space-y-1">
                <p><strong>Creado:</strong> {formatDate(reconciliation.created_at)}{(reconciliation.created_by_name || reconciliation.created_by) ? ` · por ${reconciliation.created_by_name || reconciliation.created_by}` : ''}</p>
                {reconciliation.completed_at && (
                  <p><strong>Completada:</strong> {formatDate(reconciliation.completed_at)}{(reconciliation.completed_by_name || reconciliation.completed_by) ? ` · por ${reconciliation.completed_by_name || reconciliation.completed_by}` : ''}</p>
                )}
                {reconciliation.reopened_at && (
                  <>
                    <p className="text-amber-700"><strong>Reabierta:</strong> {formatDate(reconciliation.reopened_at)}{(reconciliation.reopened_by_name || reconciliation.reopened_by) ? ` · por ${reconciliation.reopened_by_name || reconciliation.reopened_by}` : ''}</p>
                    {reconciliation.reopen_reason && <p className="text-amber-700 italic">Motivo: "{reconciliation.reopen_reason}"</p>}
                  </>
                )}
                {reconciliation.status === 'completed' && (
                  <p className="text-emerald-700 mt-1">🔒 Bloqueo bancario activo sobre {reconciliation.cash_bank_name} del {formatDate(reconciliation.period_start)} al {formatDate(reconciliation.period_end)}.</p>
                )}
              </CardContent>
            </Card>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
          {reconciliation?.status === 'draft' && canComplete && (
            <Button onClick={onComplete} disabled={Math.abs(reconciliation?.difference || 0) > 0.01} data-testid="complete-reconciliation-btn">
              <CheckCircle className="w-4 h-4 mr-1" />Completar Conciliación
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// v3.34.2 — Ver arqueo (lectura, mismo patrón que ViewReconciliationDialog)
export function ViewCashCountDialog({ open, onOpenChange, cashCount, adjustments = [], onClose }) {
  if (!cashCount) return null;

  const signedSum = (adjustments || []).reduce((s, a) => {
    const amt = Number(a.amount || 0);
    return s + (a.adjustment_type === 'debit' ? amt : -amt);
  }, 0);
  const diff = Number(cashCount.difference || 0);
  const residual = Math.round((diff - signedSum) * 100) / 100;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="view-cash-count-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Calculator className="w-5 h-5 text-blue-500" />
            Arqueo {cashCount.cash_count_number}
            {cashCount.status === 'open'
              ? <Badge className="bg-yellow-100 text-yellow-800 ml-2">Abierto</Badge>
              : <Badge className="bg-green-100 text-green-800 ml-2">Cerrado</Badge>}
            {cashCount.reopened_at && (
              <Badge variant="outline" className="ml-1 text-amber-700 border-amber-300">Reabierto</Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div className="text-sm text-muted-foreground">
            {cashCount.cash_bank_name} · Fecha {formatDate(cashCount.date)}
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Card><CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground">Saldo Teórico (inicial)</p>
              <p className="text-base font-bold">{formatCurrency(cashCount.theoretical_balance)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground">Saldo Físico</p>
              <p className="text-base font-bold">{formatCurrency(cashCount.physical_balance)}</p>
            </CardContent></Card>
            <Card><CardContent className="pt-4 pb-3">
              <p className="text-xs text-muted-foreground">Diferencia bruta</p>
              {diff === 0 ? (
                <p className="text-base font-bold text-green-600">$0,00</p>
              ) : diff > 0 ? (
                <p className="text-base font-bold text-blue-600 flex items-center gap-1"><ArrowUpCircle className="w-4 h-4" />+{formatCurrency(diff)}</p>
              ) : (
                <p className="text-base font-bold text-red-600 flex items-center gap-1"><ArrowDownCircle className="w-4 h-4" />{formatCurrency(diff)}</p>
              )}
            </CardContent></Card>
            <Card className={Math.abs(residual) < 0.01 ? 'border-green-300 bg-green-50' : 'border-orange-300 bg-orange-50'}>
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-muted-foreground">Residual (sin justificar)</p>
                <p className={`text-base font-bold ${Math.abs(residual) < 0.01 ? 'text-green-600' : (residual > 0 ? 'text-blue-600' : 'text-red-600')}`}>
                  {residual >= 0 ? '+' : ''}{formatCurrency(residual)}
                </p>
              </CardContent>
            </Card>
          </div>

          {cashCount.notes && (
            <Card className="bg-slate-50 border-slate-200">
              <CardContent className="pt-3 pb-3">
                <p className="text-xs text-muted-foreground mb-1">Notas</p>
                <p className="text-sm">{cashCount.notes}</p>
              </CardContent>
            </Card>
          )}

          <div>
            <h3 className="font-semibold mb-2">Ajustes del arqueo ({adjustments.length})</h3>
            {adjustments.length === 0 ? (
              <p className="text-xs text-muted-foreground italic">Este arqueo no tiene ajustes registrados.</p>
            ) : (
              <div className="border rounded-md overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs">N°</TableHead>
                      <TableHead className="text-xs">Tipo</TableHead>
                      <TableHead className="text-xs">Concepto</TableHead>
                      <TableHead className="text-xs">Descripción</TableHead>
                      <TableHead className="text-xs text-right">Monto</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {adjustments.map(a => (
                      <TableRow key={a.adjustment_id} data-testid={`view-cc-adj-${a.adjustment_number}`}>
                        <TableCell className="text-xs font-mono">{a.adjustment_number}</TableCell>
                        <TableCell className="text-xs">
                          {a.adjustment_type === 'debit'
                            ? <Badge className="bg-blue-100 text-blue-700">Ingreso</Badge>
                            : <Badge className="bg-red-100 text-red-700">Egreso</Badge>}
                        </TableCell>
                        <TableCell className="text-xs">{a.concept_name}</TableCell>
                        <TableCell className="text-xs max-w-[280px] truncate" title={a.description}>{a.description}</TableCell>
                        <TableCell className="text-xs text-right font-mono">
                          {a.adjustment_type === 'debit' ? '+' : '-'}{formatCurrency(a.amount)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </div>

          {/* Auditoría */}
          <Card className="bg-slate-50 border-slate-200">
            <CardContent className="pt-3 pb-3 text-xs space-y-1">
              <p><strong>Creado:</strong> {formatDate(cashCount.created_at)}{(cashCount.created_by_name || cashCount.created_by) ? ` · por ${cashCount.created_by_name || cashCount.created_by}` : ''}</p>
              {cashCount.closed_at && (
                <p><strong>Cerrado:</strong> {formatDate(cashCount.closed_at)}{(cashCount.closed_by_name || cashCount.closed_by) ? ` · por ${cashCount.closed_by_name || cashCount.closed_by}` : ''}</p>
              )}
              {cashCount.reopened_at && (
                <>
                  <p className="text-amber-700"><strong>Reabierto:</strong> {formatDate(cashCount.reopened_at)}{cashCount.reopened_by_name ? ` · por ${cashCount.reopened_by_name}` : ''}</p>
                  {cashCount.reopen_reason && <p className="text-amber-700 italic">Motivo: "{cashCount.reopen_reason}"</p>}
                </>
              )}
              {cashCount.status === 'closed' && (
                <p className="text-emerald-700 mt-1">🔒 Bloqueo arqueo activo sobre {cashCount.cash_bank_name} para la fecha {formatDate(cashCount.date)}.</p>
              )}
            </CardContent>
          </Card>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cerrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AddStatementItemDialog({ open, onOpenChange, form, setForm, incomeConcepts, expenseConcepts, onCancel, onAdd }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar Movimiento Bancario</DialogTitle>
          <p className="text-sm text-muted-foreground">Este movimiento se registrará en el Libro Diario y actualizará el saldo de la cuenta.</p>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label>Fecha *</Label>
            <Input type="date" max={today()} value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
          </div>
          <div>
            <Label>Tipo de Movimiento *</Label>
            <select value={form.concept_type} onChange={(e) => setForm({ ...form, concept_type: e.target.value, concept_id: '' })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="concept-type-select">
              <option value="expense">Egreso (Gasto bancario, comisión, etc.)</option>
              <option value="income">Ingreso (Interés, acreditación, etc.)</option>
            </select>
          </div>
          <div>
            <Label>Concepto *</Label>
            <select value={form.concept_id} onChange={(e) => setForm({ ...form, concept_id: e.target.value })} className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" data-testid="concept-select">
              <option value="">Seleccionar concepto...</option>
              {(form.concept_type === 'income' ? incomeConcepts : expenseConcepts).map(c => <option key={c.concept_id} value={c.concept_id}>{c.name}</option>)}
            </select>
          </div>
          <div>
            <Label>Descripción *</Label>
            <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Ej: Comisión mantenimiento cuenta, IVA débito fiscal..." />
          </div>
          <div>
            <Label>Monto *</Label>
            <Input type="number" step="0.01" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
          </div>
          <div>
            <Label>Notas</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button onClick={onAdd} data-testid="confirm-add-movement-btn">Agregar y Registrar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function DeleteControlDialog({ open, onOpenChange, target, onCancel, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <Trash2 className="w-5 h-5" />
            Confirmar Eliminación
          </DialogTitle>
        </DialogHeader>
        <div className="py-2">
          {target?.type === 'cash_count' && (
            <p className="text-sm">¿Eliminar el arqueo <strong>{target.item.cash_count_number}</strong> de {target.item.cash_bank_name}?<br /><span className="text-muted-foreground">Esta acción no se puede deshacer.</span></p>
          )}
          {target?.type === 'reconciliation' && (
            <p className="text-sm">¿Eliminar la conciliación <strong>{target.item.reconciliation_number}</strong> de {target.item.cash_bank_name}?<br /><span className="text-muted-foreground">Se eliminarán todos los items asociados.</span></p>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>Cancelar</Button>
          <Button variant="destructive" onClick={onConfirm} data-testid="confirm-delete-btn">Eliminar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
